/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { api } from '@/lib/api'
import type { ApiResponse, Document } from './types'

const BASE = '/api/document'

/**
 * 获取当前用户的文档列表（分页）
 */
export async function getDocuments(
  page = 0,
  size = 20
): Promise<ApiResponse<Document[]>> {
  const res = await api.get(`${BASE}?p=${page}&size=${size}`)
  return res.data
}

/**
 * 上传文档文件（普通上传）
 */
export async function uploadDocument(
  name: string,
  description: string,
  file: File
): Promise<ApiResponse<Document>> {
  const form = new FormData()
  form.append('name', name)
  form.append('description', description)
  form.append('file', file)
  const res = await api.post(BASE, form)
  return res.data
}

// ============================================================================
// 分片上传（断点续传）
// ============================================================================

const CHUNK_SIZE = 5 * 1024 * 1024 // 5MB per chunk
const MAX_CONCURRENT = 3 // 最多同时上传 3 个分片

export interface ChunkUploadProgress {
  uploadId: string
  fileName: string
  totalChunks: number
  uploadedChunks: number
  status: 'pending' | 'uploading' | 'merging' | 'done' | 'error'
  error?: string
}

/**
 * 初始化分片上传
 */
async function initChunkUpload(
  fileName: string,
  name: string,
  description: string,
  totalChunks: number,
  chunkSize: number,
  fileSize: number
): Promise<string> {
  const res = await api.post(`${BASE}/chunk/init`, {
    filename: fileName,
    name,
    description,
    total_chunks: totalChunks,
    chunk_size: chunkSize,
    file_size: fileSize,
  })
  return (res.data as { upload_id: string }).upload_id
}

/**
 * 查询已上传的分片
 */
async function getChunkStatus(uploadId: string): Promise<string[]> {
  const res = await api.get(`${BASE}/chunk/status/${uploadId}`)
  if (!(res.data as { success: boolean }).success) return []
  return ((res.data as { uploaded: string[] }).uploaded || [])
}

/**
 * 上传单个分片
 */
async function uploadChunk(
  uploadId: string,
  chunkIndex: number,
  chunk: Blob,
  onProgress?: (loaded: number, total: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const form = new FormData()
    form.append('chunk', chunk)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded, e.total)
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText)
          if (data.success) {
            resolve()
          } else {
            reject(new Error(data.message || '分片上传失败'))
          }
        } catch {
          reject(new Error('响应解析失败'))
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}`))
      }
    }

    xhr.onerror = () => reject(new Error('网络错误'))
    xhr.onabort = () => reject(new Error('上传已取消'))

    xhr.open('POST', `${BASE}/chunk/upload/${uploadId}/${chunkIndex}`)
    xhr.withCredentials = true
    // 携带鉴权头（XHR 不会自动走 axios 拦截器）
    const uid = localStorage.getItem('uid')
    if (uid) {
      xhr.setRequestHeader('New-Api-User', uid)
    }
    xhr.send(form)
  })
}

/**
 * 完成分片上传（合并分片并创建文档）
 */
async function completeChunkUpload(
  uploadId: string
): Promise<ApiResponse<Document>> {
  const res = await api.post(`${BASE}/chunk/complete`, { upload_id: uploadId })
  return res.data as ApiResponse<Document>
}

/**
 * 分片上传文件（断点续传）
 */
export async function uploadDocumentChunked(
  file: File,
  name?: string,
  description?: string,
  onProgress?: (progress: ChunkUploadProgress) => void
): Promise<ApiResponse<Document>> {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE)
  const docName = name || file.name.replace(/\.[^.]+$/, '')

  // 初始化
  const uploadId = await initChunkUpload(
    file.name,
    docName,
    description || '',
    totalChunks,
    CHUNK_SIZE,
    file.size
  )

  // 查询已上传的分片（断点续传）
  const alreadyUploaded = await getChunkStatus(uploadId)
  const uploadedSet = new Set(alreadyUploaded.map(Number))

  const progress: ChunkUploadProgress = {
    uploadId,
    fileName: file.name,
    totalChunks,
    uploadedChunks: uploadedSet.size,
    status: uploadedSet.size >= totalChunks ? 'done' : 'uploading',
  }

  // 上传缺失的分片
  const pendingChunks: number[] = []
  for (let i = 0; i < totalChunks; i++) {
    if (!uploadedSet.has(i)) {
      pendingChunks.push(i)
    }
  }

  if (onProgress) onProgress({ ...progress })

  // 并发上传剩余分片
  let currentIndex = 0
  const errors: Error[] = []

  const uploadOne = async (): Promise<void> => {
    while (currentIndex < pendingChunks.length) {
      const chunkGlobalIndex = pendingChunks[currentIndex]
      currentIndex++

      try {
        const start = chunkGlobalIndex * CHUNK_SIZE
        const end = Math.min(start + CHUNK_SIZE, file.size)
        const chunk = file.slice(start, end)

        await uploadChunk(uploadId, chunkGlobalIndex, chunk)

        progress.uploadedChunks++
        if (onProgress) onProgress({ ...progress })
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        errors.push(error)
        // 分片失败不中断其他分片，最后统一报错
      }
    }
  }

  // 启动并发 worker
  const workers = Math.min(MAX_CONCURRENT, pendingChunks.length)
  const workerPromises: Promise<void>[] = []
  for (let w = 0; w < workers; w++) {
    workerPromises.push(uploadOne())
  }

  await Promise.all(workerPromises)

  if (errors.length > 0) {
    throw new Error(`部分分片上传失败: ${errors[0].message}`)
  }

  if (progress.uploadedChunks < totalChunks) {
    throw new Error(`上传未完成 (${progress.uploadedChunks}/${totalChunks})`)
  }

  // 合并分片
  progress.status = 'merging'
  if (onProgress) onProgress({ ...progress })

  const result = await completeChunkUpload(uploadId)

  progress.status = 'done'
  if (onProgress) onProgress({ ...progress })

  return result
}

/**
 * 删除文档（软删除 + 删除物理文件）
 */
export async function deleteDocument(id: number): Promise<ApiResponse> {
  const res = await api.delete(`${BASE}/${id}`)
  return res.data
}

/**
 * 获取文档下载 URL（已废弃，请使用 getStreamUrl）
 */
export function getDownloadUrl(id: number): string {
  return `${BASE}/${id}`
}

/**
 * 获取服务端代理流 URL（走服务器内网中转，鉴权，不暴露 COS URL）
 */
export function getStreamUrl(id: number): string {
  return `${BASE}/${id}/stream`
}

/**
 * 获取文档预览 URL（PDF 走服务端代理流，Office 走 COS 预签名）
 */
export async function getPreviewUrl(
  id: number
): Promise<ApiResponse<{ url: string; name: string; type: string }>> {
  const res = await api.get(`${BASE}/${id}/preview`)
  return res.data as ApiResponse<{ url: string; name: string; type: string }>
}
